import React, { useCallback, useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  createSolarOcularVisit,
  deleteSolarOcularVisit,
  fetchSolarOcularVisitById,
  fetchSolarOcularVisits,
  fetchSolarOcularVisitTemplate,
  updateSolarOcularVisit
} from '../../api/forms';

function numberValue(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? number : 0;
}

function formatDate(value) {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    });
  } catch {
    return '-';
  }
}

function toInputDate(value) {
  if (!value) return new Date().toISOString().slice(0, 10);
  try {
    return new Date(value).toISOString().slice(0, 10);
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function safeFilePart(value, fallback) {
  return String(value || fallback)
    .trim()
    .replace(/[^a-z0-9_-]+/gi, '-')
    .replace(/^-+|-+$/g, '');
}

function ocularFileName(visit) {
  return [
    safeFilePart(visit?.ocularNumber, 'solar-ocular-visit'),
    safeFilePart(visit?.projectName, 'project'),
    safeFilePart(visit?.clientName, 'client')
  ].filter(Boolean).join('-') + '.pdf';
}

function companyLogoSrc(company = {}) {
  return company.logo || `${window.location.origin}/logo.png`;
}

async function imageUrlToDataUrl(url) {
  if (!url) return '';
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || '');
      reader.onerror = () => resolve('');
      reader.readAsDataURL(blob);
    });
  } catch {
    return '';
  }
}

function imageFormatFromDataUrl(dataUrl = '') {
  return /^data:image\/jpe?g/i.test(dataUrl) ? 'JPEG' : 'PNG';
}

async function imageDimensionsFromDataUrl(dataUrl = '') {
  if (!String(dataUrl || '').startsWith('data:image/')) return null;
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({
      width: image.naturalWidth || image.width || 1,
      height: image.naturalHeight || image.height || 1
    });
    image.onerror = () => resolve(null);
    image.src = dataUrl;
  });
}

function fitImageInsideBox(dimensions, maxWidth, maxHeight) {
  const sourceWidth = Math.max(1, dimensions?.width || maxWidth);
  const sourceHeight = Math.max(1, dimensions?.height || maxHeight);
  const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: Math.max(1, sourceWidth * scale),
    height: Math.max(1, sourceHeight * scale)
  };
}

function normalizeSitePhotos(visit = {}) {
  const photos = Array.isArray(visit.sitePhotos) ? visit.sitePhotos : [];
  return [visit.sitePhotoDataUrl, ...photos]
    .map((photo) => String(photo || '').trim())
    .filter((photo) => photo.startsWith('data:image/'))
    .filter((photo, index, list) => list.indexOf(photo) === index)
    .slice(0, 12);
}

async function fileToCompressedDataUrl(file, maxSide = 1400, quality = 0.78) {
  if (!file) return '';
  const rawDataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.width * scale));
      canvas.height = Math.max(1, Math.round(image.height * scale));
      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    image.onerror = () => resolve(rawDataUrl);
    image.src = rawDataUrl;
  });
}

async function prepareLogoForPdf(dataUrl) {
  if (!String(dataUrl || '').startsWith('data:image/')) return '';
  return await new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const source = document.createElement('canvas');
      source.width = Math.max(1, image.naturalWidth || image.width);
      source.height = Math.max(1, image.naturalHeight || image.height);
      const sourceContext = source.getContext('2d');
      sourceContext.drawImage(image, 0, 0, source.width, source.height);

      const pixels = sourceContext.getImageData(0, 0, source.width, source.height).data;
      let minX = source.width;
      let minY = source.height;
      let maxX = 0;
      let maxY = 0;
      for (let y = 0; y < source.height; y += 1) {
        for (let x = 0; x < source.width; x += 1) {
          const index = ((y * source.width) + x) * 4;
          const alpha = pixels[index + 3];
          const red = pixels[index];
          const green = pixels[index + 1];
          const blue = pixels[index + 2];
          const isVisible = alpha > 24 && !(red > 248 && green > 248 && blue > 248);
          if (isVisible) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      if (minX > maxX || minY > maxY) {
        resolve(dataUrl);
        return;
      }

      const pad = 4;
      const sx = Math.max(0, minX + 2);
      const sy = Math.max(0, minY - pad);
      const sw = Math.min(source.width - sx, (maxX - sx) + pad);
      const sh = Math.min(source.height - sy, (maxY - minY) + (pad * 2));
      const output = document.createElement('canvas');
      output.width = Math.max(1, sw);
      output.height = Math.max(1, sh);
      const outputContext = output.getContext('2d');
      outputContext.fillStyle = '#ffffff';
      outputContext.fillRect(0, 0, output.width, output.height);
      outputContext.drawImage(source, sx, sy, sw, sh, 0, 0, output.width, output.height);
      resolve(output.toDataURL('image/png'));
    };
    image.onerror = () => resolve(dataUrl);
    image.src = dataUrl;
  });
}

function normalizeVisit(visit = {}) {
  const clientSignature = { name: '', signatureDataUrl: '', signedAt: null, ...(visit.clientSignature || {}) };
  const inspectorSignature = { name: '', signatureDataUrl: '', signedAt: null, ...(visit.inspectorSignature || {}) };
  const sitePhotos = normalizeSitePhotos(visit);
  if (!clientSignature.name) clientSignature.name = visit.clientName || '';
  if (!inspectorSignature.name) inspectorSignature.name = visit.siteInspector || '';
  return {
    ...visit,
    status: visit.status || 'draft',
    visitDate: toInputDate(visit.visitDate),
    sitePhotoDataUrl: sitePhotos[0] || '',
    sitePhotos,
    electricity: { utility: '', averageMonthlyBill: 0, averageMonthlyConsumptionKwh: 0, electricityRate: 0, highestMonthlyBill: 0, lowestMonthlyBill: 0, ...(visit.electricity || {}) },
    loadAssessment: { daytimeLoadKw: 0, nighttimeLoadKw: 0, peakLoadKw: 0, criticalLoads: '', airconQuantity: 0, airconHpPerUnit: '', airconHoursPerDay: 0, pumpQuantity: 0, pumpHpKw: '', pumpHoursPerDay: 0, ...(visit.loadAssessment || {}) },
    electricalSystem: { phaseType: '', utilityVoltage: '', measuredVoltage: '', mainBreakerRating: '', serviceEntranceWireSize: '', meterType: '', mainPanelCondition: '', remarks: '', ...(visit.electricalSystem || {}) },
    roof: { lengthM: 0, widthM: 0, usableAreaSqm: 0, orientation: '', ...(visit.roof || {}) },
    shading: { bestSolarExposureArea: '', shadingLevel: '', treesRequireTrimming: '', ...(visit.shading || {}) },
    proposedPanels: { panelCount: 0, panelWattageW: 0, pvCapacityKwp: 0, futureExpansion: '', ...(visit.proposedPanels || {}) },
    inverter: { distanceToMainPanelM: 0, proposedSizeKw: 0, ...(visit.inverter || {}) },
    battery: { requiredMode: '', requiredCapacityKwh: 0, proposedCount: 0, ...(visit.battery || {}) },
    cableRoute: { panelToInverterDistanceM: 0, inverterToMainPanelDistanceM: 0, batteryToInverterDistanceM: 0, specialRequirements: '', ...(visit.cableRoute || {}) },
    monitoring: { routerLocation: '', distanceToInverterM: 0, ...(visit.monitoring || {}) },
    access: { specialConcerns: '', ...(visit.access || {}) },
    recommendedSystem: { panelCount: 0, panelWattageW: 0, totalPvCapacityKwp: 0, hybridInverterKw: 0, batteryCount: 0, batteryKwh: 0, totalBatteryCapacityKwh: 0, dailyProductionKwh: 0, monthlyProductionKwh: 0, backupHours: '', ...(visit.recommendedSystem || {}) },
    summary: { roofCondition: '', electricalCondition: '', solarExposure: '', installationDifficulty: '', recommendation: '', inspectorRemarks: '', ...(visit.summary || {}) },
    sections: Array.isArray(visit.sections) ? visit.sections : [],
    clientSignature,
    inspectorSignature
  };
}

function Field({ label, value, onChange, type = 'text', textarea = false, children }) {
  return (
    <label className="block min-w-0 text-sm">
      <span className="mb-1 block text-xs font-medium text-gray-500">{label}</span>
      {children || (textarea ? (
        <textarea rows={3} value={value || ''} onChange={(event) => onChange(event.target.value)} className="min-h-24 w-full rounded border px-3 py-2 text-base sm:text-sm" />
      ) : (
        <input type={type} value={value ?? ''} onChange={(event) => onChange(event.target.value)} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm" />
      ))}
    </label>
  );
}

function SignatureCanvas({ value, onChange, readOnly = false, label = 'Signature' }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const pointRef = useRef(null);

  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const context = canvas.getContext('2d');
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.lineWidth = 2;
    context.strokeStyle = '#111827';
    return context;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || readOnly) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = getContext();
    if (context) context.scale(ratio, ratio);
    if (value) {
      const image = new Image();
      image.onload = () => {
        const ctx = getContext();
        if (ctx) ctx.drawImage(image, 0, 0, rect.width, rect.height);
      };
      image.src = value;
    }
  }, [getContext, readOnly, value]);

  useEffect(() => {
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [resizeCanvas]);

  const pointFromEvent = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event) => {
    event.preventDefault();
    canvasRef.current?.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    pointRef.current = pointFromEvent(event);
  };

  const move = (event) => {
    if (!drawingRef.current) return;
    event.preventDefault();
    const nextPoint = pointFromEvent(event);
    const context = getContext();
    if (!context || !pointRef.current) return;
    context.beginPath();
    context.moveTo(pointRef.current.x, pointRef.current.y);
    context.lineTo(nextPoint.x, nextPoint.y);
    context.stroke();
    pointRef.current = nextPoint;
  };

  const stop = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    pointRef.current = null;
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (canvas && context) context.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return readOnly ? (
    value ? <img src={value} alt={label} className="h-40 w-full rounded border bg-white object-contain" /> : <p className="text-sm text-gray-500">No signature provided.</p>
  ) : (
    <div>
      <canvas
        ref={canvasRef}
        aria-label={`${label} drawing area`}
        className="h-40 w-full touch-none rounded border bg-white"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerCancel={stop}
      />
      <button type="button" onClick={clear} className="mt-2 min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Clear signature</button>
    </div>
  );
}

function checkedRows(section) {
  const checked = (section.items || []).filter((item) => item.checked);
  if (!checked.length) return '-';
  return checked.map((item) => item.note ? `${item.label} (${item.note})` : item.label).join(', ');
}

function addFooter(doc, companyName, ocularNumber) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageCount = doc.internal.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFontSize(7);
    doc.setTextColor(75, 85, 99);
    doc.text(`${companyName} | ${ocularNumber || ''}`, 42, 812);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - 88, 812);
  }
  doc.setTextColor(0, 0, 0);
}

async function buildOcularPdf(visit, company = {}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 42;
  const contentWidth = pageWidth - (margin * 2);
  const companyName = company.name || 'iConnect Home Wireless Network & Data Solution';
  const companyAddress = company.address || 'Door 1 Agunod Bldg., Lapu-Lapu street, Magugpo Poblacion, Tagum City';
  const companyPhone = company.phone || '09562755878';
  const companyEmail = company.email || 'iconnect0718@gmail.com';
  const logo = await prepareLogoForPdf(await imageUrlToDataUrl(companyLogoSrc(company)));
  const sitePhotos = normalizeSitePhotos(visit);
  const logoWidth = 132;
  const logoHeight = 96;
  const logoX = pageWidth - margin - logoWidth;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('SOLAR PV SYSTEM OCULAR VISIT CHECKLIST', margin, 54);
  doc.setFontSize(10);
  doc.text(companyName, margin, 82);
  doc.setFont('helvetica', 'normal');
  doc.text(companyAddress, margin, 96);
  doc.text(`${companyPhone} | ${companyEmail}`, margin, 110);
  if (logo) doc.addImage(logo, 'PNG', logoX, 30, logoWidth, logoHeight, undefined, 'FAST');

  autoTable(doc, {
    startY: 128,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Ocular No.', visit.ocularNumber || '-', 'Date', formatDate(visit.visitDate)],
      ['Client Name', visit.clientName || '-', 'Contact Number', visit.contactNumber || '-'],
      ['Email', visit.email || '-', 'Site Inspector', visit.siteInspector || '-'],
      ['Project / Business Name', visit.projectName || '-', 'Google Maps / Pin', visit.googleMapsLocation || '-'],
      ['Installation Address', visit.installationAddress || '-', 'Status', visit.status || 'draft']
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 115 }, 2: { fontStyle: 'bold', cellWidth: 115 } }
  });

  let y = doc.lastAutoTable.finalY + 14;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Target Budget', visit.targetBudget || '-', 'Preferred Payment', visit.preferredPayment || '-'],
      ['Electric Utility', visit.electricity?.utility || '-', 'Average Monthly Bill', visit.electricity?.averageMonthlyBill || '-'],
      ['Average kWh / Month', visit.electricity?.averageMonthlyConsumptionKwh || '-', 'Rate / kWh', visit.electricity?.electricityRate || '-'],
      ['Highest Bill', visit.electricity?.highestMonthlyBill || '-', 'Lowest Bill', visit.electricity?.lowestMonthlyBill || '-']
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 115 }, 2: { fontStyle: 'bold', cellWidth: 115 } }
  });

  y = doc.lastAutoTable.finalY + 14;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Daytime Load kW', visit.loadAssessment?.daytimeLoadKw || '-', 'Nighttime Load kW', visit.loadAssessment?.nighttimeLoadKw || '-'],
      ['Peak Load kW', visit.loadAssessment?.peakLoadKw || '-', 'Critical Loads', visit.loadAssessment?.criticalLoads || '-'],
      ['Aircon Load', `${visit.loadAssessment?.airconQuantity || '-'} unit(s), ${visit.loadAssessment?.airconHpPerUnit || '-'} HP, ${visit.loadAssessment?.airconHoursPerDay || '-'} hrs/day`, 'Water Pump Load', `${visit.loadAssessment?.pumpQuantity || '-'} unit(s), ${visit.loadAssessment?.pumpHpKw || '-'}, ${visit.loadAssessment?.pumpHoursPerDay || '-'} hrs/day`]
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 115 }, 2: { fontStyle: 'bold', cellWidth: 115 } }
  });

  y = doc.lastAutoTable.finalY + 14;
  const fieldRows = [
    ['Electrical', `Phase: ${visit.electricalSystem?.phaseType || '-'} | Utility V: ${visit.electricalSystem?.utilityVoltage || '-'} | Measured V: ${visit.electricalSystem?.measuredVoltage || '-'} | Breaker: ${visit.electricalSystem?.mainBreakerRating || '-'} | Wire: ${visit.electricalSystem?.serviceEntranceWireSize || '-'} | Meter: ${visit.electricalSystem?.meterType || '-'} | Panel: ${visit.electricalSystem?.mainPanelCondition || '-'}`],
    ['Roof', `Length: ${visit.roof?.lengthM || '-'}m | Width: ${visit.roof?.widthM || '-'}m | Usable: ${visit.roof?.usableAreaSqm || '-'} sqm | Orientation: ${visit.roof?.orientation || '-'}`],
    ['Shading', `Best area: ${visit.shading?.bestSolarExposureArea || '-'} | Level: ${visit.shading?.shadingLevel || '-'} | Trees trimming: ${visit.shading?.treesRequireTrimming || '-'}`],
    ['Panel Proposal', `${visit.proposedPanels?.panelCount || '-'} pcs x ${visit.proposedPanels?.panelWattageW || '-'}W | ${visit.proposedPanels?.pvCapacityKwp || '-'} kWp | Future expansion: ${visit.proposedPanels?.futureExpansion || '-'}`],
    ['Inverter', `Distance to main panel: ${visit.inverter?.distanceToMainPanelM || '-'}m | Proposed size: ${visit.inverter?.proposedSizeKw || '-'}kW`],
    ['Battery', `Requirement: ${visit.battery?.requiredMode || '-'} | Capacity: ${visit.battery?.requiredCapacityKwh || '-'}kWh | Proposed count: ${visit.battery?.proposedCount || '-'}`],
    ['Cable Route', `PV to inverter: ${visit.cableRoute?.panelToInverterDistanceM || '-'}m | Inverter to panel: ${visit.cableRoute?.inverterToMainPanelDistanceM || '-'}m | Battery to inverter: ${visit.cableRoute?.batteryToInverterDistanceM || '-'}m | ${visit.cableRoute?.specialRequirements || ''}`],
    ['Monitoring', `Router: ${visit.monitoring?.routerLocation || '-'} | Distance: ${visit.monitoring?.distanceToInverterM || '-'}m`],
    ['Access Concerns', visit.access?.specialConcerns || '-']
  ];
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: fieldRows,
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 105 }, 1: { cellWidth: contentWidth - 105 } }
  });

  y = doc.lastAutoTable.finalY + 14;
  if (sitePhotos.length) {
    const photoGap = 10;
    const photoColumns = sitePhotos.length > 1 ? 2 : 1;
    const maxImageWidth = (contentWidth - (photoGap * (photoColumns - 1))) / photoColumns;
    const maxImageHeight = sitePhotos.length > 1 ? 145 : 170;
    if (y + 20 > pageHeight - 48) {
      doc.addPage();
      y = 42;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`Site Photo${sitePhotos.length > 1 ? 's' : ''}`, margin, y);
    y += 10;

    let rowHeight = 0;
    for (let index = 0; index < sitePhotos.length; index += 1) {
      const photo = sitePhotos[index];
      const imageDimensions = await imageDimensionsFromDataUrl(photo);
      const fittedImage = fitImageInsideBox(imageDimensions, maxImageWidth, maxImageHeight);
      const column = index % photoColumns;
      if (column === 0 && y + fittedImage.height > pageHeight - 48) {
        doc.addPage();
        y = 42;
        rowHeight = 0;
      }
      const x = margin + (column * (maxImageWidth + photoGap));
      doc.addImage(
        photo,
        imageFormatFromDataUrl(photo),
        x,
        y,
        fittedImage.width,
        fittedImage.height,
        undefined,
        'FAST'
      );
      rowHeight = Math.max(rowHeight, fittedImage.height);
      if (column === photoColumns - 1 || index === sitePhotos.length - 1) {
        y += rowHeight + 12;
        rowHeight = 0;
      }
    }
  }

  (visit.sections || []).forEach((section, index) => {
    if (y > 690) {
      doc.addPage();
      y = 42;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(`${index + 1}. ${section.title || ''}`, margin, y);
    y += 6;
    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
      body: (section.items || []).map((item) => [item.checked ? '[x]' : '[ ]', item.note ? `${item.label}\nNote: ${item.note}` : item.label]),
      columnStyles: { 0: { cellWidth: 30, halign: 'center' } }
    });
    y = doc.lastAutoTable.finalY + 12;
  });

  if (y > 610) {
    doc.addPage();
    y = 42;
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Initial Recommended System', margin, y);
  y += 8;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Solar Panels', `${visit.recommendedSystem?.panelCount || '-'} pcs x ${visit.recommendedSystem?.panelWattageW || '-'}W`],
      ['Total PV Capacity', `${visit.recommendedSystem?.totalPvCapacityKwp || '-'} kWp`],
      ['Hybrid Inverter', `${visit.recommendedSystem?.hybridInverterKw || '-'} kW`],
      ['Battery', `${visit.recommendedSystem?.batteryCount || '-'} pcs x ${visit.recommendedSystem?.batteryKwh || '-'} kWh | Total ${visit.recommendedSystem?.totalBatteryCapacityKwh || '-'} kWh`],
      ['Estimated Production', `${visit.recommendedSystem?.dailyProductionKwh || '-'} kWh/day | ${visit.recommendedSystem?.monthlyProductionKwh || '-'} kWh/month`],
      ['Backup Requirement', visit.recommendedSystem?.backupHours || '-']
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 130 } }
  });

  y = doc.lastAutoTable.finalY + 14;
  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 4, lineColor: [209, 213, 219], lineWidth: 0.5 },
    body: [
      ['Roof Condition', visit.summary?.roofCondition || '-', 'Electrical Condition', visit.summary?.electricalCondition || '-'],
      ['Solar Exposure', visit.summary?.solarExposure || '-', 'Installation Difficulty', visit.summary?.installationDifficulty || '-'],
      ['Recommendation', visit.summary?.recommendation || '-', 'Inspector Remarks', visit.summary?.inspectorRemarks || '-']
    ],
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 115 }, 2: { fontStyle: 'bold', cellWidth: 115 } }
  });
  y = doc.lastAutoTable.finalY + 20;

  if (y > 650) {
    doc.addPage();
    y = 42;
  }
  const sigWidth = 190;
  const sigHeight = 60;
  const rightX = pageWidth - margin - sigWidth;
  const clientSig = visit.clientSignature || {};
  const inspectorSig = visit.inspectorSignature || {};
  doc.setFont('helvetica', 'bold');
  doc.text('Client Confirmation', margin, y);
  y += 22;
  if (clientSig.signatureDataUrl) doc.addImage(clientSig.signatureDataUrl, 'PNG', margin + 10, y, sigWidth - 20, sigHeight - 6);
  if (inspectorSig.signatureDataUrl) doc.addImage(inspectorSig.signatureDataUrl, 'PNG', rightX + 10, y, sigWidth - 20, sigHeight - 6);
  doc.line(margin, y + sigHeight, margin + sigWidth, y + sigHeight);
  doc.line(rightX, y + sigHeight, rightX + sigWidth, y + sigHeight);
  doc.setFontSize(8);
  doc.text(clientSig.name || visit.clientName || '', margin, y + sigHeight + 14);
  doc.text(inspectorSig.name || visit.siteInspector || '', rightX, y + sigHeight + 14);
  doc.setFont('helvetica', 'normal');
  doc.text('Client / Customer', margin, y + sigHeight + 27);
  doc.text('Site Inspector', rightX, y + sigHeight + 27);
  addFooter(doc, companyName, visit.ocularNumber);
  return doc;
}

export default function SolarOcularVisitManager({ authConfig, onUnauthorized, company, initialOpenId, employeeMode = false }) {
  const [visits, setVisits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [error, setError] = useState('');
  const [previewPdfUrl, setPreviewPdfUrl] = useState('');
  const lastInitialOpenId = useRef('');
  const saveInFlight = useRef(false);
  const pendingPhotoUploads = useRef(0);
  const readOnly = employeeMode && Boolean(selected?._id);
  const editorLocked = readOnly || (employeeMode && saving);

  const handleError = useCallback((err, fallback) => {
    const message = err?.response?.data?.error || err?.message || fallback;
    setError(message);
    if (err?.response?.status === 401 && onUnauthorized) onUnauthorized();
  }, [onUnauthorized]);

  const loadVisits = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await fetchSolarOcularVisits(search.trim() ? { reference: search.trim() } : {}, authConfig);
      setVisits(rows);
    } catch (err) {
      handleError(err, 'Failed to load ocular visits.');
    } finally {
      setLoading(false);
    }
  }, [authConfig, handleError, search]);

  useEffect(() => {
    loadVisits();
  }, [loadVisits]);

  useEffect(() => () => {
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
  }, [previewPdfUrl]);

  const openVisit = useCallback(async (id) => {
    setError('');
    try {
      const visit = await fetchSolarOcularVisitById(id, authConfig);
      setSelected(normalizeVisit(visit));
      setPreviewPdfUrl('');
    } catch (err) {
      handleError(err, 'Failed to open ocular visit.');
    }
  }, [authConfig, handleError]);

  useEffect(() => {
    if (!initialOpenId || lastInitialOpenId.current === initialOpenId) return;
    lastInitialOpenId.current = initialOpenId;
    openVisit(initialOpenId);
  }, [initialOpenId, openVisit]);

  const createVisit = async () => {
    setSaving(true);
    setError('');
    try {
      const visit = employeeMode
        ? await fetchSolarOcularVisitTemplate(authConfig)
        : await createSolarOcularVisit({}, authConfig);
      setSelected(normalizeVisit(visit));
      if (!employeeMode) await loadVisits();
    } catch (err) {
      handleError(err, 'Failed to create ocular visit.');
    } finally {
      setSaving(false);
    }
  };

  const changeSelected = (updater) => setSelected((current) => (
    !current || (employeeMode && (current._id || saveInFlight.current)) ? current : updater(current)
  ));

  const updateField = (field, value) => changeSelected((current) => ({ ...current, [field]: value }));
  const updateNested = (group, field, value) => changeSelected((current) => ({
    ...current,
    [group]: { ...(current[group] || {}), [field]: value }
  }));
  const updateSectionItem = (sectionIndex, itemIndex, field, value) => changeSelected((current) => ({
    ...current,
    sections: current.sections.map((section, sIndex) => (
      sIndex === sectionIndex
        ? {
          ...section,
          items: section.items.map((item, iIndex) => (
            iIndex === itemIndex ? { ...item, [field]: value } : item
          ))
        }
        : section
    ))
  }));

  const handleSitePhotoChange = async (event) => {
    if (editorLocked) return;
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    pendingPhotoUploads.current += 1;
    setUploadingPhotos(true);
    setError('');
    try {
      const dataUrls = await Promise.all(files.map((file) => fileToCompressedDataUrl(file)));
      changeSelected((current) => {
        const sitePhotos = normalizeSitePhotos({
          ...current,
          sitePhotos: [...normalizeSitePhotos(current), ...dataUrls]
        });
        return {
          ...current,
          sitePhotoDataUrl: sitePhotos[0] || '',
          sitePhotos
        };
      });
    } catch (err) {
      handleError(err, 'Failed to read selected site photo.');
    } finally {
      pendingPhotoUploads.current -= 1;
      setUploadingPhotos(pendingPhotoUploads.current > 0);
    }
  };

  const removeSitePhoto = (photoIndex) => {
    changeSelected((current) => {
      const sitePhotos = normalizeSitePhotos(current).filter((_, index) => index !== photoIndex);
      return {
        ...current,
        sitePhotoDataUrl: sitePhotos[0] || '',
        sitePhotos
      };
    });
  };

  const clearSitePhotos = () => changeSelected((current) => ({
    ...current,
    sitePhotoDataUrl: '',
    sitePhotos: []
  }));

  const saveVisit = async () => {
    if (!selected || readOnly || saveInFlight.current || (employeeMode && pendingPhotoUploads.current) || (!employeeMode && !selected._id)) return;
    saveInFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const saved = employeeMode
        ? await createSolarOcularVisit(selected, authConfig)
        : await updateSolarOcularVisit(selected._id, selected, authConfig);
      setSelected(normalizeVisit(saved));
      await loadVisits();
    } catch (err) {
      handleError(err, 'Failed to save ocular visit.');
    } finally {
      saveInFlight.current = false;
      setSaving(false);
    }
  };

  const removeVisit = async () => {
    if (employeeMode || !selected?._id) return;
    if (!window.confirm(`Delete ${selected.ocularNumber || 'this ocular visit'}?`)) return;
    setSaving(true);
    setError('');
    try {
      await deleteSolarOcularVisit(selected._id, authConfig);
      setSelected(null);
      await loadVisits();
    } catch (err) {
      handleError(err, 'Failed to delete ocular visit.');
    } finally {
      setSaving(false);
    }
  };

  const savePdf = async () => {
    const doc = await buildOcularPdf(selected, company);
    doc.save(ocularFileName(selected));
  };

  const printPdf = async () => {
    const doc = await buildOcularPdf(selected, company);
    doc.autoPrint();
    const url = URL.createObjectURL(doc.output('blob'));
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const previewPdf = async () => {
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    const doc = await buildOcularPdf(selected, company);
    setPreviewPdfUrl(URL.createObjectURL(doc.output('blob')));
  };

  if (selected) {
    const selectedSitePhotos = normalizeSitePhotos(selected);
    return (
      <div className="rounded border bg-white">
        <div className="flex flex-col gap-3 border-b p-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs text-gray-500">{selected.ocularNumber || 'New ocular visit'}</p>
            <h3 className="text-lg font-semibold">Solar System Ocular Visit</h3>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex">
            <button type="button" onClick={previewPdf} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Preview</button>
            <button type="button" onClick={printPdf} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Print</button>
            <button type="button" onClick={savePdf} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Save PDF</button>
            {!readOnly && <button type="button" onClick={saveVisit} disabled={saving || (employeeMode && uploadingPhotos)} className="min-h-10 rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save'}</button>}
            <button type="button" onClick={() => setSelected(null)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Close</button>
          </div>
        </div>
        {error && <p className="m-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <div className="space-y-5 p-3">
          {employeeMode && <p className="text-sm text-gray-600">{readOnly ? 'This form has been saved. Employees can view and print saved forms; only management can edit or delete them.' : 'Complete the form before saving. Once saved, only management can edit or delete it.'}</p>}
          <fieldset disabled={editorLocked} className="min-w-0 space-y-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Date" type="date" value={selected.visitDate} onChange={(value) => updateField('visitDate', value)} />
            <Field label="Status" value={selected.status} onChange={(value) => updateField('status', value)}>
              <select value={selected.status || 'draft'} onChange={(event) => updateField('status', event.target.value)} className="min-h-11 w-full rounded border px-3 py-2 text-base sm:text-sm">
                <option value="draft">Draft</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </Field>
            <Field label="Client Name" value={selected.clientName} onChange={(value) => updateField('clientName', value)} />
            <Field label="Contact Number" value={selected.contactNumber} onChange={(value) => updateField('contactNumber', value)} />
            <Field label="Email" value={selected.email} onChange={(value) => updateField('email', value)} />
            <Field label="Project / Business Name" value={selected.projectName} onChange={(value) => updateField('projectName', value)} />
            <Field label="Google Maps Location / Pin" value={selected.googleMapsLocation} onChange={(value) => updateField('googleMapsLocation', value)} />
            <Field label="Site Inspector" value={selected.siteInspector} onChange={(value) => updateField('siteInspector', value)} />
            <div className="sm:col-span-2 lg:col-span-4">
              <Field label="Installation Address" value={selected.installationAddress} onChange={(value) => updateField('installationAddress', value)} textarea />
            </div>
          </div>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Client Requirements</h4>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Target Budget" value={selected.targetBudget} onChange={(value) => updateField('targetBudget', value)} />
              <Field label="Preferred Payment" value={selected.preferredPayment} onChange={(value) => updateField('preferredPayment', value)} />
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Electricity Bill & Consumption</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Electric Utility" value={selected.electricity.utility} onChange={(value) => updateNested('electricity', 'utility', value)} />
              <Field label="Average Monthly Bill" type="number" value={selected.electricity.averageMonthlyBill} onChange={(value) => updateNested('electricity', 'averageMonthlyBill', numberValue(value))} />
              <Field label="Average Monthly Consumption (kWh)" type="number" value={selected.electricity.averageMonthlyConsumptionKwh} onChange={(value) => updateNested('electricity', 'averageMonthlyConsumptionKwh', numberValue(value))} />
              <Field label="Electricity Rate / kWh" type="number" value={selected.electricity.electricityRate} onChange={(value) => updateNested('electricity', 'electricityRate', numberValue(value))} />
              <Field label="Highest Monthly Bill" type="number" value={selected.electricity.highestMonthlyBill} onChange={(value) => updateNested('electricity', 'highestMonthlyBill', numberValue(value))} />
              <Field label="Lowest Monthly Bill" type="number" value={selected.electricity.lowestMonthlyBill} onChange={(value) => updateNested('electricity', 'lowestMonthlyBill', numberValue(value))} />
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Load Assessment</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Estimated Daytime Load (kW)" type="number" value={selected.loadAssessment.daytimeLoadKw} onChange={(value) => updateNested('loadAssessment', 'daytimeLoadKw', numberValue(value))} />
              <Field label="Estimated Nighttime Load (kW)" type="number" value={selected.loadAssessment.nighttimeLoadKw} onChange={(value) => updateNested('loadAssessment', 'nighttimeLoadKw', numberValue(value))} />
              <Field label="Estimated Peak Load (kW)" type="number" value={selected.loadAssessment.peakLoadKw} onChange={(value) => updateNested('loadAssessment', 'peakLoadKw', numberValue(value))} />
              <Field label="Aircon Quantity" type="number" value={selected.loadAssessment.airconQuantity} onChange={(value) => updateNested('loadAssessment', 'airconQuantity', numberValue(value))} />
              <Field label="Aircon HP / Unit" value={selected.loadAssessment.airconHpPerUnit} onChange={(value) => updateNested('loadAssessment', 'airconHpPerUnit', value)} />
              <Field label="Aircon Hours / Day" type="number" value={selected.loadAssessment.airconHoursPerDay} onChange={(value) => updateNested('loadAssessment', 'airconHoursPerDay', numberValue(value))} />
              <Field label="Water Pump Quantity" type="number" value={selected.loadAssessment.pumpQuantity} onChange={(value) => updateNested('loadAssessment', 'pumpQuantity', numberValue(value))} />
              <Field label="Water Pump HP / kW" value={selected.loadAssessment.pumpHpKw} onChange={(value) => updateNested('loadAssessment', 'pumpHpKw', value)} />
              <Field label="Pump Hours / Day" type="number" value={selected.loadAssessment.pumpHoursPerDay} onChange={(value) => updateNested('loadAssessment', 'pumpHoursPerDay', numberValue(value))} />
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="Critical Loads During Brownout" value={selected.loadAssessment.criticalLoads} onChange={(value) => updateNested('loadAssessment', 'criticalLoads', value)} textarea />
              </div>
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Site Measurements & Proposed System</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Phase Type" value={selected.electricalSystem.phaseType} onChange={(value) => updateNested('electricalSystem', 'phaseType', value)} />
              <Field label="Utility Voltage" value={selected.electricalSystem.utilityVoltage} onChange={(value) => updateNested('electricalSystem', 'utilityVoltage', value)} />
              <Field label="Measured Voltage" value={selected.electricalSystem.measuredVoltage} onChange={(value) => updateNested('electricalSystem', 'measuredVoltage', value)} />
              <Field label="Main Breaker Rating" value={selected.electricalSystem.mainBreakerRating} onChange={(value) => updateNested('electricalSystem', 'mainBreakerRating', value)} />
              <Field label="Service Entrance Wire Size" value={selected.electricalSystem.serviceEntranceWireSize} onChange={(value) => updateNested('electricalSystem', 'serviceEntranceWireSize', value)} />
              <Field label="Meter Type" value={selected.electricalSystem.meterType} onChange={(value) => updateNested('electricalSystem', 'meterType', value)} />
              <Field label="Main Panel Condition" value={selected.electricalSystem.mainPanelCondition} onChange={(value) => updateNested('electricalSystem', 'mainPanelCondition', value)} />
              <Field label="Roof Orientation" value={selected.roof.orientation} onChange={(value) => updateNested('roof', 'orientation', value)} />
              <Field label="Roof Length (m)" type="number" value={selected.roof.lengthM} onChange={(value) => updateNested('roof', 'lengthM', numberValue(value))} />
              <Field label="Roof Width (m)" type="number" value={selected.roof.widthM} onChange={(value) => updateNested('roof', 'widthM', numberValue(value))} />
              <Field label="Usable Area (sqm)" type="number" value={selected.roof.usableAreaSqm} onChange={(value) => updateNested('roof', 'usableAreaSqm', numberValue(value))} />
              <Field label="Shading Level" value={selected.shading.shadingLevel} onChange={(value) => updateNested('shading', 'shadingLevel', value)} />
              <Field label="Proposed Panels (pcs)" type="number" value={selected.proposedPanels.panelCount} onChange={(value) => updateNested('proposedPanels', 'panelCount', numberValue(value))} />
              <Field label="Panel Wattage (W)" type="number" value={selected.proposedPanels.panelWattageW} onChange={(value) => updateNested('proposedPanels', 'panelWattageW', numberValue(value))} />
              <Field label="Estimated PV Capacity (kWp)" type="number" value={selected.proposedPanels.pvCapacityKwp} onChange={(value) => updateNested('proposedPanels', 'pvCapacityKwp', numberValue(value))} />
              <Field label="Future Expansion" value={selected.proposedPanels.futureExpansion} onChange={(value) => updateNested('proposedPanels', 'futureExpansion', value)} />
              <Field label="Proposed Inverter Size (kW)" type="number" value={selected.inverter.proposedSizeKw} onChange={(value) => updateNested('inverter', 'proposedSizeKw', numberValue(value))} />
              <Field label="Inverter to Main Panel (m)" type="number" value={selected.inverter.distanceToMainPanelM} onChange={(value) => updateNested('inverter', 'distanceToMainPanelM', numberValue(value))} />
              <Field label="Battery Requirement" value={selected.battery.requiredMode} onChange={(value) => updateNested('battery', 'requiredMode', value)} />
              <Field label="Battery Capacity Needed (kWh)" type="number" value={selected.battery.requiredCapacityKwh} onChange={(value) => updateNested('battery', 'requiredCapacityKwh', numberValue(value))} />
              <Field label="Battery Count" type="number" value={selected.battery.proposedCount} onChange={(value) => updateNested('battery', 'proposedCount', numberValue(value))} />
              <Field label="PV to Inverter Distance (m)" type="number" value={selected.cableRoute.panelToInverterDistanceM} onChange={(value) => updateNested('cableRoute', 'panelToInverterDistanceM', numberValue(value))} />
              <Field label="Inverter to Panel Distance (m)" type="number" value={selected.cableRoute.inverterToMainPanelDistanceM} onChange={(value) => updateNested('cableRoute', 'inverterToMainPanelDistanceM', numberValue(value))} />
              <Field label="Battery to Inverter Distance (m)" type="number" value={selected.cableRoute.batteryToInverterDistanceM} onChange={(value) => updateNested('cableRoute', 'batteryToInverterDistanceM', numberValue(value))} />
              <Field label="Router Location" value={selected.monitoring.routerLocation} onChange={(value) => updateNested('monitoring', 'routerLocation', value)} />
              <Field label="Router to Inverter Distance (m)" type="number" value={selected.monitoring.distanceToInverterM} onChange={(value) => updateNested('monitoring', 'distanceToInverterM', numberValue(value))} />
              <div className="sm:col-span-2 lg:col-span-4">
                <Field label="Electrical Remarks" value={selected.electricalSystem.remarks} onChange={(value) => updateNested('electricalSystem', 'remarks', value)} textarea />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <Field label="Best Solar Exposure Area" value={selected.shading.bestSolarExposureArea} onChange={(value) => updateNested('shading', 'bestSolarExposureArea', value)} textarea />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <Field label="Cable Route Special Requirements" value={selected.cableRoute.specialRequirements} onChange={(value) => updateNested('cableRoute', 'specialRequirements', value)} textarea />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <Field label="Installation Access Special Concerns" value={selected.access.specialConcerns} onChange={(value) => updateNested('access', 'specialConcerns', value)} textarea />
              </div>
            </div>
          </section>

          <section className="rounded border p-3">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h4 className="font-semibold">Site Photos</h4>
                <p className="text-xs text-gray-500">Upload roof, panel board, inverter location, battery location, cable route, and other ocular photos.</p>
              </div>
              {!readOnly && selectedSitePhotos.length > 0 && (
                <button type="button" onClick={clearSitePhotos} className="min-h-10 rounded border px-3 py-2 text-sm text-red-600 hover:bg-red-50">Remove all photos</button>
              )}
            </div>
            {!readOnly && <input
              type="file"
              aria-label="Site photos"
              accept="image/*"
              multiple
              onChange={handleSitePhotoChange}
              className="block min-h-11 w-full rounded border px-3 py-2 text-sm"
            />}
            {uploadingPhotos && <p className="text-sm text-gray-500">Processing photos...</p>}
            {selectedSitePhotos.length > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {selectedSitePhotos.map((photo, index) => (
                  <div key={`${photo.slice(0, 32)}-${index}`} className="rounded border bg-gray-50 p-2">
                    <img src={photo} alt={`Ocular site ${index + 1}`} className="h-48 w-full rounded border bg-white object-contain" />
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-500">Photo {index + 1}</span>
                      {!readOnly && <button type="button" onClick={() => removeSitePhoto(index)} className="min-h-9 rounded border px-3 py-1 text-xs text-red-600 hover:bg-red-50">Remove</button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Checklist</h4>
            <div className="grid gap-3 lg:grid-cols-2">
              {(selected.sections || []).map((section, sectionIndex) => (
                <div key={`${section.title}-${sectionIndex}`} className="rounded border">
                  <div className="border-b bg-gray-50 px-3 py-2">
                    <p className="font-medium">{section.title}</p>
                    <p className="text-xs text-gray-500">{checkedRows(section)}</p>
                  </div>
                  <div className="divide-y">
                    {(section.items || []).map((item, itemIndex) => (
                      <div key={`${item.label}-${itemIndex}`} className="grid gap-2 p-2 sm:grid-cols-[minmax(0,1fr)_minmax(180px,240px)] sm:items-center">
                        <label className="flex min-h-10 items-center gap-2 text-sm">
                          <input type="checkbox" checked={Boolean(item.checked)} onChange={(event) => updateSectionItem(sectionIndex, itemIndex, 'checked', event.target.checked)} />
                          <span>{item.label}</span>
                        </label>
                        <input value={item.note || ''} onChange={(event) => updateSectionItem(sectionIndex, itemIndex, 'note', event.target.value)} placeholder="Note / other details" className="min-h-10 rounded border px-3 py-2 text-sm" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Initial Recommended System</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label="Solar Panels (pcs)" type="number" value={selected.recommendedSystem.panelCount} onChange={(value) => updateNested('recommendedSystem', 'panelCount', numberValue(value))} />
              <Field label="Panel Wattage (W)" type="number" value={selected.recommendedSystem.panelWattageW} onChange={(value) => updateNested('recommendedSystem', 'panelWattageW', numberValue(value))} />
              <Field label="Total PV Capacity (kWp)" type="number" value={selected.recommendedSystem.totalPvCapacityKwp} onChange={(value) => updateNested('recommendedSystem', 'totalPvCapacityKwp', numberValue(value))} />
              <Field label="Hybrid Inverter (kW)" type="number" value={selected.recommendedSystem.hybridInverterKw} onChange={(value) => updateNested('recommendedSystem', 'hybridInverterKw', numberValue(value))} />
              <Field label="Battery Count" type="number" value={selected.recommendedSystem.batteryCount} onChange={(value) => updateNested('recommendedSystem', 'batteryCount', numberValue(value))} />
              <Field label="Battery kWh Each" type="number" value={selected.recommendedSystem.batteryKwh} onChange={(value) => updateNested('recommendedSystem', 'batteryKwh', numberValue(value))} />
              <Field label="Total Battery kWh" type="number" value={selected.recommendedSystem.totalBatteryCapacityKwh} onChange={(value) => updateNested('recommendedSystem', 'totalBatteryCapacityKwh', numberValue(value))} />
              <Field label="Backup Requirement" value={selected.recommendedSystem.backupHours} onChange={(value) => updateNested('recommendedSystem', 'backupHours', value)} />
              <Field label="Daily Production kWh" type="number" value={selected.recommendedSystem.dailyProductionKwh} onChange={(value) => updateNested('recommendedSystem', 'dailyProductionKwh', numberValue(value))} />
              <Field label="Monthly Production kWh" type="number" value={selected.recommendedSystem.monthlyProductionKwh} onChange={(value) => updateNested('recommendedSystem', 'monthlyProductionKwh', numberValue(value))} />
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Site Condition Summary</h4>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <Field label="Roof Condition" value={selected.summary.roofCondition} onChange={(value) => updateNested('summary', 'roofCondition', value)} />
              <Field label="Electrical Condition" value={selected.summary.electricalCondition} onChange={(value) => updateNested('summary', 'electricalCondition', value)} />
              <Field label="Solar Exposure" value={selected.summary.solarExposure} onChange={(value) => updateNested('summary', 'solarExposure', value)} />
              <Field label="Installation Difficulty" value={selected.summary.installationDifficulty} onChange={(value) => updateNested('summary', 'installationDifficulty', value)} />
              <Field label="Recommendation" value={selected.summary.recommendation} onChange={(value) => updateNested('summary', 'recommendation', value)} />
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="Inspector Remarks" value={selected.summary.inspectorRemarks} onChange={(value) => updateNested('summary', 'inspectorRemarks', value)} textarea />
              </div>
            </div>
          </section>

          <section className="rounded border p-3">
            <h4 className="mb-3 font-semibold">Client Confirmation & Signatures</h4>
            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <Field label="Client Name" value={selected.clientSignature.name} onChange={(value) => updateNested('clientSignature', 'name', value)} />
                <div className="mt-2">
                  <SignatureCanvas readOnly={editorLocked} label="Client signature" value={selected.clientSignature.signatureDataUrl} onChange={(value) => updateNested('clientSignature', 'signatureDataUrl', value)} />
                </div>
              </div>
              <div>
                <Field label="Site Inspector" value={selected.inspectorSignature.name} onChange={(value) => updateNested('inspectorSignature', 'name', value)} />
                <div className="mt-2">
                  <SignatureCanvas readOnly={editorLocked} label="Inspector signature" value={selected.inspectorSignature.signatureDataUrl} onChange={(value) => updateNested('inspectorSignature', 'signatureDataUrl', value)} />
                </div>
              </div>
            </div>
          </section>

          </fieldset>
          {previewPdfUrl && (
            <section className="rounded border bg-gray-100 p-3">
              <div className="mb-2 text-center text-sm text-gray-500">A4 PDF preview</div>
              <iframe title="Solar ocular visit PDF preview" src={previewPdfUrl} className="h-[70vh] w-full rounded border bg-white" />
            </section>
          )}

          {!readOnly && <div className="flex flex-col gap-2 border-t pt-3 sm:flex-row sm:justify-end">
            {!employeeMode && <button type="button" onClick={removeVisit} className="min-h-10 rounded border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50">Delete</button>}
            <button type="button" onClick={saveVisit} disabled={saving || (employeeMode && uploadingPhotos)} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-60">{saving ? 'Saving...' : 'Save ocular visit'}</button>
          </div>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <p className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      <div className="grid gap-2 rounded border bg-gray-50 p-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <Field label="Search ocular no., client, project, address, inspector" value={search} onChange={setSearch} />
        <button type="button" onClick={loadVisits} className="min-h-10 rounded border px-4 py-2 text-sm hover:bg-white">Search</button>
        <button type="button" onClick={createVisit} disabled={saving} className="min-h-10 rounded bg-indigo-600 px-4 py-2 text-sm text-white disabled:opacity-60">{saving ? 'Creating...' : 'New ocular visit'}</button>
      </div>
      <div className="grid gap-3 md:hidden">
        {loading && <p className="rounded border bg-white p-4 text-sm text-gray-500">Loading ocular visits...</p>}
        {!loading && visits.length === 0 && <p className="rounded border bg-white p-4 text-sm text-gray-500">No ocular visits found.</p>}
        {visits.map((visit) => (
          <button key={visit._id} type="button" onClick={() => openVisit(visit._id)} className="rounded border bg-white p-3 text-left shadow-sm">
            <p className="font-semibold">{visit.ocularNumber}</p>
            <p className="text-sm text-gray-700">{visit.clientName || '-'}</p>
            <p className="text-xs text-gray-500">{formatDate(visit.visitDate)} | {visit.status}</p>
          </button>
        ))}
      </div>
      <div className="hidden overflow-auto rounded border md:block">
        <table className="w-full min-w-[840px] text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 text-left">Ocular No.</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Client</th>
              <th className="p-2 text-left">Project</th>
              <th className="p-2 text-left">Inspector</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="7" className="p-4 text-center text-gray-500">Loading ocular visits...</td></tr>
            )}
            {!loading && visits.length === 0 && (
              <tr><td colSpan="7" className="p-4 text-center text-gray-500">No ocular visits found.</td></tr>
            )}
            {visits.map((visit) => (
              <tr key={visit._id} className="border-t">
                <td className="p-2 font-medium">{visit.ocularNumber}</td>
                <td className="p-2">{formatDate(visit.visitDate)}</td>
                <td className="p-2">{visit.clientName || '-'}</td>
                <td className="p-2">{visit.projectName || '-'}</td>
                <td className="p-2">{visit.siteInspector || '-'}</td>
                <td className="p-2 capitalize">{visit.status || 'draft'}</td>
                <td className="p-2 text-right">
                  <button type="button" onClick={() => openVisit(visit._id)} className="min-h-10 rounded border px-3 py-2 text-sm hover:bg-gray-100">Open</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
